/**
 * Color utilities for score-based visualizations
 */

/**
 * Get a color based on a score (0-1)
 * Returns the platform's 3 signal bands (good/warn/bad), matching the
 * eval-results hero + phase-strip thresholds. Returns a CSS var() string —
 * valid in both inline `style` color props and SVG `stroke`/`fill`.
 */
export function getScoreColor(score: number): string {
  if (score >= 0.8) return 'var(--color-signal-good)';
  if (score >= 0.6) return 'var(--color-signal-warn)';
  return 'var(--color-signal-bad)';
}

/**
 * Get a Tailwind color class based on a score (0-1)
 * Mirrors the same 3 signal bands as getScoreColor above.
 */
export function getScoreColorClass(score: number): string {
  if (score >= 0.8) return 'text-signal-good bg-signal-good/10';
  if (score >= 0.6) return 'text-signal-warn bg-signal-warn/10';
  return 'text-signal-bad bg-signal-bad/10';
}

/**
 * Get badge variant based on score
 */
export function getScoreBadgeVariant(score: number): 'success' | 'warning' | 'error' {
  if (score >= 0.7) return 'success';
  if (score >= 0.4) return 'warning';
  return 'error';
}

/**
 * Get region color for annotations (distinct colors for different regions)
 */
export function getRegionColor(index: number): string {
  const colors = [
    '#3b82f6', // blue-500
    '#8b5cf6', // violet-500
    '#ec4899', // pink-500
    '#f59e0b', // amber-500
    '#10b981', // emerald-500
    '#06b6d4', // cyan-500
    '#f97316', // orange-500
    '#a855f7', // purple-500
  ];
  return colors[index % colors.length];
}

/**
 * Convert color to rgba format with specified alpha channel
 * Supports both hex colors (#RRGGBB) and existing rgba strings
 */
export function hexToRgba(color: string | undefined | null, alpha: number): string {
  // Default fallback color if undefined/null
  if (!color) {
    return `rgba(128, 128, 128, ${alpha})`; // gray
  }

  // If already in rgba format, extract RGB values and apply new alpha
  if (color.startsWith('rgba(')) {
    const match = color.match(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*[\d.]+\)/);
    if (match) {
      return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
    }
  }

  // If in rgb format (no alpha), extract RGB values and add alpha
  if (color.startsWith('rgb(')) {
    const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (match) {
      return `rgba(${match[1]}, ${match[2]}, ${match[3]}, ${alpha})`;
    }
  }

  // Handle hex color format
  if (color.startsWith('#')) {
    // Validate hex format
    if (color.length !== 7) {
      console.warn(`Invalid hex color format: ${color}, using fallback`);
      return `rgba(128, 128, 128, ${alpha})`;
    }

    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);

    // Validate parsed values
    if (isNaN(r) || isNaN(g) || isNaN(b)) {
      console.warn(`Failed to parse hex color: ${color}, using fallback`);
      return `rgba(128, 128, 128, ${alpha})`;
    }

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // Unknown format, return as-is or fallback
  console.warn(`Unknown color format: ${color}, using fallback`);
  return `rgba(128, 128, 128, ${alpha})`;
}

/**
 * Get error severity color
 */
export function getErrorSeverityColor(severity: 'low' | 'medium' | 'high'): string {
  switch (severity) {
    case 'low':
      return '#eab308'; // yellow-500
    case 'medium':
      return '#f97316'; // orange-500
    case 'high':
      return '#ef4444'; // red-500
  }
}

/**
 * Normalize any color format to rgba
 * This ensures consistent color handling across the app
 */
export function normalizeColor(color: string | undefined | null): string {
  if (!color) {
    return 'rgba(128, 128, 128, 0.5)'; // gray with default alpha
  }

  // If already rgba, return as-is
  if (color.startsWith('rgba(')) {
    return color;
  }

  // If rgb, convert to rgba with default alpha
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', ', 1)');
  }

  // If hex, convert to rgba with default alpha
  if (color.startsWith('#')) {
    return hexToRgba(color, 1);
  }

  // Fallback
  return 'rgba(128, 128, 128, 0.5)';
}

/**
 * Importance level colors for annotations
 * Used for body region annotations to indicate priority
 */
export const IMPORTANCE_COLORS = {
  critical: '#dc2626',  // red-600
  high: '#ea580c',      // orange-600
  medium: '#f59e0b',    // amber-500
  low: '#16a34a',       // green-600
} as const;

/**
 * Get color for an importance level
 */
export function getImportanceColor(importance: 'low' | 'medium' | 'high' | 'critical'): string {
  return IMPORTANCE_COLORS[importance];
}

/**
 * Get Tailwind classes for importance badge styling
 */
export function getImportanceBadgeClasses(importance: 'low' | 'medium' | 'high' | 'critical'): string {
  switch (importance) {
    case 'critical':
      return 'bg-danger/15 text-danger border-danger/30';
    case 'high':
      return 'bg-accent-orange/15 text-accent-orange border-accent-orange/30';
    case 'medium':
      return 'bg-warning/15 text-warning border-warning/30';
    case 'low':
      return 'bg-success/15 text-success border-success/30';
  }
}
